import type {
  FilterableSquawkStatus,
  QuotePublic,
  Severity,
  SquawkCustomerReviewPublic,
  SquawkDetailPublic,
} from "@common/client"

export const STAGES = [
  "needs_review",
  "accepted",
  "rejected",
  "accepted_verified",
  "rejected_verified",
] as const

export type Stage = (typeof STAGES)[number]

export const STATUSES: FilterableSquawkStatus[] = [
  "pending_review",
  "approved",
  "rejected",
  "verified",
]

export function stageOf(squawk: SquawkDetailPublic): Stage {
  switch (squawk.status) {
    case "draft":
    case "pending_review":
      return "needs_review"
    case "approved":
      return "accepted"
    case "verified":
      return "accepted_verified"
    case "rejected":
      return squawk.verified_at ? "rejected_verified" : "rejected"
  }
}

export function stageLabel(stage: Stage): string {
  switch (stage) {
    case "needs_review":
      return "Needs review"
    case "accepted":
      return "Accepted"
    case "rejected":
      return "Rejected"
    case "accepted_verified":
      return "Accepted · verified"
    case "rejected_verified":
      return "Rejected · verified"
  }
}

export function statusLabel(status: FilterableSquawkStatus): string {
  switch (status) {
    case "pending_review":
      return "Needs review"
    case "approved":
      return "Accepted"
    case "rejected":
      return "Rejected"
    case "verified":
      return "Verified"
  }
}

export function statusStage(status: FilterableSquawkStatus): Stage {
  switch (status) {
    case "pending_review":
      return "needs_review"
    case "approved":
      return "accepted"
    case "rejected":
      return "rejected"
    case "verified":
      return "accepted_verified"
  }
}

export const NEW_SQUAWK_DESCRIPTION = "New squawk"

export function isFreshDraft(squawk: SquawkDetailPublic): boolean {
  return (
    squawk.status === "draft" && squawk.description === NEW_SQUAWK_DESCRIPTION
  )
}

export function isDecided(squawk: SquawkDetailPublic): boolean {
  return squawk.status !== "pending_review" && squawk.status !== "draft"
}

export function isVerified(squawk: SquawkDetailPublic): boolean {
  return squawk.status === "verified" || squawk.verified_at !== null
}

export function awaitsVerification(squawk: SquawkDetailPublic): boolean {
  return isDecided(squawk) && !isVerified(squawk)
}

export function isQuoted(squawk: SquawkDetailPublic): boolean {
  return squawk.quote_task_id !== null
}

export function canQuote(squawk: SquawkDetailPublic): boolean {
  return (
    squawk.status === "verified" &&
    squawk.work_package_id !== null &&
    squawk.archived_at === null &&
    !isQuoted(squawk)
  )
}

export function canPrintCard(squawk: SquawkDetailPublic): boolean {
  return (
    squawk.status !== "draft" &&
    squawk.status !== "rejected" &&
    squawk.archived_at === null
  )
}

export function needsRevision(
  quote: QuotePublic,
  canReview: boolean | undefined,
): boolean {
  return !(
    quote.disposition === "draft" ||
    (canReview &&
      (quote.disposition === "review" ||
        quote.disposition === "customer_response"))
  )
}

export function detailsOpen(squawk: SquawkDetailPublic): boolean {
  return !isDecided(squawk) && squawk.archived_at === null
}

export function chargesOpen(squawk: SquawkDetailPublic): boolean {
  return squawk.quote_task_id === null && squawk.archived_at === null
}

export function workOpen(squawk: SquawkDetailPublic): boolean {
  return !isVerified(squawk) && chargesOpen(squawk)
}

export function pricingOpen(squawk: SquawkDetailPublic): boolean {
  const closedRejection = squawk.status === "rejected" && isVerified(squawk)
  return chargesOpen(squawk) && !closedRejection
}

export function quoteHref(
  quoteId: string,
  returnTo: string,
): {
  to: "/quotes/$quoteId"
  params: { quoteId: string }
  search: { return_to: string }
} {
  return {
    to: "/quotes/$quoteId",
    params: { quoteId },
    search: { return_to: returnTo },
  }
}

export const SEVERITIES: (Severity | null)[] = [
  "airworthiness",
  "informational",
  null,
]

export const NO_SEVERITY = "none"

export type SeverityChoice = Severity | typeof NO_SEVERITY

export function severityChoice(severity: Severity | null): SeverityChoice {
  return severity ?? NO_SEVERITY
}

export function severityLabel(severity: Severity | null): string {
  switch (severity) {
    case "airworthiness":
      return "Airworthiness"
    case "informational":
      return "Informational"
    case null:
      return "None"
  }
}

export type CustomerState = "sent" | "approved" | "declined" | "asked"

export function customerState(
  review: SquawkCustomerReviewPublic,
): CustomerState {
  switch (review.status) {
    case "pending":
      return "sent"
    case "approved":
      return "approved"
    case "rejected":
      return "declined"
    case "clarification_requested":
      return "asked"
  }
}

export function customerLabel(state: CustomerState): string {
  switch (state) {
    case "sent":
      return "Sent"
    case "approved":
      return "Approved"
    case "declined":
      return "Declined"
    case "asked":
      return "Asked"
  }
}

export function needsAnswer(squawk: SquawkDetailPublic): boolean {
  const review = squawk.customer_review
  return review != null && customerState(review) === "asked"
}

export function reporterName(squawk: SquawkDetailPublic): string {
  const user = squawk.logged_by ?? squawk.created_by
  return user ? `${user.first_name} ${user.last_name}`.trim() : ""
}
